import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LinkProps } from 'react-router';
import { Link } from '../lib/router';
import { cx } from '../lib/cx';
import { Icon, type IconName } from './Icon';
import { Spinner } from './feedback';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-contrast shadow-[0_1px_2px_rgb(0_0_0/0.16),inset_0_1px_0_rgb(255_255_255/0.2)] hover:brightness-108 active:translate-y-px active:brightness-95',
  secondary:
    'bg-surface text-fg border border-border-strong/80 shadow-[0_1px_2px_rgb(0_0_0/0.03)] hover:bg-surface-2 hover:border-border-strong active:translate-y-px',
  ghost: 'bg-transparent text-fg hover:bg-surface-2/80 active:bg-surface-2',
  danger:
    'bg-danger text-white shadow-[0_1px_2px_rgb(0_0_0/0.16),inset_0_1px_0_rgb(255_255_255/0.2)] hover:brightness-108 active:translate-y-px',
  subtle: 'bg-brand-soft text-brand ring-1 ring-inset ring-brand/20 hover:brightness-95 active:translate-y-px',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-xs font-semibold tracking-tight gap-1.5 rounded-md',
  md: 'min-h-11 px-4 text-sm font-semibold tracking-tight gap-2 rounded-lg',
  lg: 'min-h-13 px-5 text-base font-semibold tracking-tight gap-2.5 rounded-xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconEnd?: IconName;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
}

function buttonClass(variant: ButtonVariant, size: ButtonSize, block: boolean | undefined, className: string | undefined) {
  return cx(
    'inline-flex items-center justify-center select-none transition-all duration-150',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
    VARIANTS[variant],
    SIZES[size],
    block && 'w-full',
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, iconEnd, loading, block, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, block, className)}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 22 : 18} /> : icon && <Icon name={icon} size={size === 'lg' ? 22 : 18} />}
      {children}
      {iconEnd && !loading && <Icon name={iconEnd} size={size === 'lg' ? 22 : 18} />}
    </button>
  );
});

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  block?: boolean;
}

/** A link that looks like a Button — never wrap a Button in a Link (a button inside a link is invalid HTML). */
export function ButtonLink({ variant = 'primary', size = 'md', icon, block, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass(variant, size, block, typeof className === 'string' ? className : undefined)} {...rest}>
      {icon && <Icon name={icon} size={size === 'lg' ? 22 : 18} />}
      {children}
    </Link>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function IconButton({ icon, label, variant = 'ghost', size = 'md', className, type = 'button', ...rest }: IconButtonProps) {
  const dim = size === 'sm' ? 'size-9 rounded-md' : size === 'lg' ? 'size-13 rounded-xl' : 'size-11 rounded-lg';
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx('inline-flex shrink-0 items-center justify-center transition-colors disabled:opacity-50', VARIANTS[variant], dim, className)}
      {...rest}
    >
      <Icon name={icon} size={size === 'lg' ? 22 : 18} />
    </button>
  );
}
